import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  ButtonBase,
  IconButton,
  Tooltip,
  useTheme,
  alpha,
  Alert,
  CircularProgress,
  type SxProps,
  type Theme,
} from "@mui/material";
import MoveToInboxIcon from "@mui/icons-material/MoveToInbox";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AddIcon from "@mui/icons-material/Add";
import SmsIcon from "@mui/icons-material/Sms";
import { LEAD_TEXTS_CHIP_LABEL, leadTextsOptIn } from "../../lib/leadSmsOptIn";

import {
  type DashboardLeadRow,
  formatLeadMoveInDate,
} from "../../lib/dashboardLeads";
import {
  buildLeadDisplayName,
  buildLeadLocationParts,
} from "../../lib/leadPreferences";
import { runLeadAction, type LeadActionKind } from "../../services/leadConversionService";
import { leadActionErrorMessage } from "../../lib/leadRefusal";
import type { UnclaimedLeadTruth } from "../../services/leadOpportunityService";
import { TOUCH_TARGET } from "../../theme/theme";
import {
  compareLeadOpportunity,
  contactConfidenceChipColor,
  contactConfidenceLabel,
  firstShotLabel,
  firstShotState,
  revenueOpportunityLabel,
  revenueOpportunityReasons,
} from "../../lib/leadOpportunity";

// ---------------------------------------------------------------------------
// Pure selectors / formatters (unit-tested via selectNewLeads)
// ---------------------------------------------------------------------------

/**
 * The "New Leads" claim feed: every lead that is still UNCLAIMED, highest
 * revenue opportunity first, then newest.
 *
 * Ch76 · "unclaimed" is decided by the BACKEND stamp (`lead.unclaimed`, from
 * lead_flow_truth.is_unclaimed — the same predicate the Admin page's
 * `leads.unclaimed` counts with). This selector used to carry its own
 * predicate (`status != "converted"` and no email-matched client), which is how
 * the Admin page said 12 while this widget said "all caught up". When the stamp
 * is present it is the verdict; the browser only layers the optimistic local
 * resolve on top (a claimed/converted row leaves the list on the next render
 * before the refetch — see ApartmentDashboard's onResolved). The legacy
 * predicate survives ONLY for payloads that predate the stamp.
 */
export function isUnclaimedRow(lead: DashboardLeadRow): boolean {
  const notConvertedLocally = (lead.status ?? "new") !== "converted";
  if (typeof lead.unclaimed === "boolean") {
    return lead.unclaimed && notConvertedLocally;
  }
  if (lead.unclaimed === null) {
    // The backend refused to guess (ledger unreadable). Show nothing rather
    // than a confident list — the card's envelope says UNKNOWN beside it.
    return false;
  }
  return notConvertedLocally && !lead.resolved_assigned_agent_id;
}

export function selectNewLeads(
  leads: ReadonlyArray<DashboardLeadRow>
): DashboardLeadRow[] {
  return leads.filter(isUnclaimedRow).sort(compareLeadOpportunity);
}

/**
 * What the badge and the empty state say, given the one all-time figure.
 * Pure so it can be pinned: the card must never say "all caught up" while the
 * Admin page counts unclaimed leads it cannot show.
 */
export function describeUnclaimed(
  shown: number,
  truth: UnclaimedLeadTruth | null | undefined,
  windowDays: number
): { badge: string | null; empty: string; hiddenNote: string | null } {
  if (!truth) {
    return {
      badge: shown > 0 ? String(shown) : null,
      empty: "You're all caught up — no unclaimed leads.",
      hiddenNote: null,
    };
  }
  if (truth.status !== "ok" || truth.total === null) {
    return {
      badge: "UNKNOWN",
      empty: "Unclaimed count is UNKNOWN — the claim ledger could not be read. Refresh to retry.",
      hiddenNote: null,
    };
  }
  const hidden = Math.max(truth.total - shown, 0);
  // The note rides above the rows; with no rows the empty state says it instead.
  const hiddenNote =
    hidden > 0 && shown > 0
      ? `${hidden} more unclaimed ${hidden === 1 ? "lead is" : "leads are"} older than ${windowDays} days — see View all leads.`
      : null;
  return {
    badge: truth.total > 0 ? String(truth.total) : null,
    empty:
      truth.total === 0
        ? "You're all caught up — no unclaimed leads."
        : `${truth.total} unclaimed ${truth.total === 1 ? "lead is" : "leads are"} older than ${windowDays} days and not shown here — see View all leads.`,
    hiddenNote,
  };
}

function formatLeadBudgetLabel(lead: DashboardLeadRow): string {
  const textBudget = (lead.budget || "").trim();
  if (textBudget) return textBudget;
  if (
    typeof lead.budget_number === "number" &&
    Number.isFinite(lead.budget_number)
  ) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(lead.budget_number);
  }
  return "Budget missing";
}

function formatLeadNeighborhoodLabel(lead: DashboardLeadRow): string {
  const parts = buildLeadLocationParts(
    lead.neighborhoods,
    lead.neighborhood_other
  );
  if (parts.length === 0) return "";
  const visible = parts.slice(0, 2).join(", ");
  return parts.length > 2 ? `${visible} +${parts.length - 2}` : visible;
}

// ---------------------------------------------------------------------------
// Row — the day-panel lead row, lifted into a theme-aware feed row.
// Anatomy: [⊕ dashed claim circle] [name / "Neighborhoods · Move-in M/D"] [$budget, right, blue]
// ---------------------------------------------------------------------------

const LeadFeedRow: React.FC<{
  lead: DashboardLeadRow;
  nowMs: number;
  onRowClick: (lead: DashboardLeadRow) => void | Promise<void>;
  pendingAction?: LeadActionKind;
  error?: string;
  onAction: (lead: DashboardLeadRow, action: LeadActionKind) => void;
}> = ({ lead, nowMs, onRowClick, pendingAction, error, onAction }) => {
  const theme = useTheme();
  const title =
    buildLeadDisplayName(lead) ||
    lead.email ||
    lead.phone ||
    "Unnamed lead";
  const budget = formatLeadBudgetLabel(lead);
  const budgetMissing = budget === "Budget missing";
  const neighborhood = formatLeadNeighborhoodLabel(lead);
  const moveInDate = formatLeadMoveInDate(lead.move_in_date);

  return (
    <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}`, py: 0.5 }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        px: 0.5,
        "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.06) },
        borderRadius: 1,
      }}
    >
      {/* One-tap convert — dashed circle-plus, mirrors the Incoming Leads panel. */}
      <Box
        sx={{
          flexShrink: 0,
          width: TOUCH_TARGET,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Tooltip title="Convert to client" placement="left">
          <IconButton
            size="small"
            aria-label={`Convert ${title} to client`}
            disabled={Boolean(pendingAction)}
            onClick={(e) => {
              e.stopPropagation();
              onAction(lead, "convert");
            }}
            sx={{
              minWidth: TOUCH_TARGET,
              minHeight: TOUCH_TARGET,
              width: TOUCH_TARGET,
              height: TOUCH_TARGET,
              color: "primary.main",
              border: `1px dashed ${alpha(theme.palette.primary.main, 0.45)}`,
              bgcolor: "transparent",
              transition: "background-color 120ms ease, border-color 120ms ease",
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.14),
                borderColor: theme.palette.primary.main,
                borderStyle: "solid",
              },
            }}
          >
            {pendingAction === "convert" ? (
              <CircularProgress size={15} aria-label="Converting lead" />
            ) : (
              <AddIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>
    <ButtonBase
      aria-label={`Open ${title}`}
      onClick={() => void onRowClick(lead)}
      sx={{
        flex: 1,
        minWidth: 0,
        textAlign: "left",
        minHeight: 48,
        py: 1,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        transition: "background-color 160ms ease",
      }}
    >

      {/* Middle: name + right-aligned budget, then criteria caption */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) auto",
            columnGap: 1,
            alignItems: "center",
          }}
        >
          <Typography
            variant="body2"
            fontWeight={700}
            noWrap
            sx={{ color: "text.primary", lineHeight: 1.25 }}
          >
            {title}
          </Typography>
          <Typography
            variant="caption"
            fontWeight={700}
            noWrap
            sx={{
              lineHeight: 1.25,
              color: budgetMissing ? "text.disabled" : "primary.main",
            }}
          >
            {budget}
          </Typography>
        </Box>
        {(neighborhood || moveInDate) && (
          // gotcha: MUI caption is an inline span — force block so stacked
          // caption lines don't concatenate.
          <Typography
            variant="caption"
            noWrap
            sx={{
              display: "block",
              color: "text.secondary",
              lineHeight: 1.3,
              mt: 0.25,
            }}
          >
            {neighborhood}
            {neighborhood && moveInDate && (
              <Box component="span" sx={{ color: "text.disabled", mx: 0.5 }}>
                ·
              </Box>
            )}
            {moveInDate && <>Move-in {moveInDate}</>}
          </Typography>
        )}
        <Box sx={{ display: "flex", gap: 0.5, mt: 0.75, flexWrap: "wrap" }}>
          {/* Ch78 · the form's SMS tick comes FIRST: it is the cheapest next
              action on the board and was rendered nowhere before. */}
          {leadTextsOptIn(lead) === "yes" && (
            <Chip
              size="small"
              icon={<SmsIcon sx={{ fontSize: 13 }} />}
              label={LEAD_TEXTS_CHIP_LABEL}
              color="success"
              variant="outlined"
              data-testid={`lead-texts-ok-${lead.id}`}
              sx={{ height: 22, "& .MuiChip-label": { px: 0.75, fontSize: "0.68rem", fontWeight: 700 } }}
            />
          )}
          <Chip
            size="small"
            label={revenueOpportunityLabel(lead)}
            color={lead.revenue_opportunity_band === "high" ? "primary" : "default"}
            variant={lead.revenue_opportunity_band === "high" ? "filled" : "outlined"}
            sx={{ height: 22, "& .MuiChip-label": { px: 0.75, fontSize: "0.68rem" } }}
          />
          <Chip
            size="small"
            label={contactConfidenceLabel(lead)}
            // Scorer v2: color logic lives in contactConfidenceChipColor
            // (leadOpportunity.ts) so the FAKE-tier veto is unit-pinned.
            color={contactConfidenceChipColor(lead)}
            variant="outlined"
            sx={{ height: 22, "& .MuiChip-label": { px: 0.75, fontSize: "0.68rem" } }}
          />
          {(() => {
            const shot = firstShotState(lead, nowMs);
            const label = firstShotLabel(shot);
            if (!shot || !label) return null;
            return (
              <Chip
                size="small"
                label={label}
                color={shot.phase === "window" ? "warning" : "error"}
                variant={shot.phase === "window" ? "outlined" : "filled"}
                sx={{ height: 22, "& .MuiChip-label": { px: 0.75, fontSize: "0.68rem", fontWeight: 700 } }}
              />
            );
          })()}
        </Box>
        <Typography
          variant="caption"
          sx={{ display: "block", color: "text.secondary", lineHeight: 1.3, mt: 0.5 }}
        >
          {revenueOpportunityReasons(lead)}
        </Typography>
      </Box>
    </ButtonBase>
    </Box>
    {error && (
      <Alert severity="warning" role="status" sx={{ mx: 1, mt: 0.5, py: 0 }}>
        {error}
      </Alert>
    )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Feed — the full card. Replaces "Needs Attention" for elevated operators.
// ---------------------------------------------------------------------------

export interface NewLeadsFeedProps {
  leads: DashboardLeadRow[];
  onRowClick: (lead: DashboardLeadRow) => void | Promise<void>;
  onViewAll: () => void;
  onResolved: (lead: DashboardLeadRow, ownerUserId: string, action: LeadActionKind) => void;
  /** Extra Card styling (height matching, margins) from the call site. */
  cardSx?: SxProps<Theme>;
  unavailable?: boolean;
  /**
   * PKT-0616 · true until the dashboard's FIRST fetch has settled. Proven live
   * (2026-09-02, bundle 7072f27b, visibility-state timeline): a tab that loads
   * hidden holds its refresh until it is foregrounded (singleFlightRefresh
   * `isAllowed`), and for that whole window this card said "You're all caught
   * up — no unclaimed leads." with nothing loaded — a false all-clear the
   * Director read as "the fetch never fires". Not-yet-loaded is UNKNOWN, never
   * zero (DoD MONEY row), so the empty copy must wait for a settled load.
   */
  loading?: boolean;
  /** Ch76 · the Admin page's all-time `leads.unclaimed` figure, same computation. */
  unclaimedTruth?: UnclaimedLeadTruth | null;
  /** The feed's own window, so the card can say what it cannot show. */
  windowDays?: number;
}

const NewLeadsFeed: React.FC<NewLeadsFeedProps> = ({
  leads,
  onRowClick,
  onViewAll,
  onResolved,
  cardSx,
  unavailable = false,
  loading = false,
  unclaimedTruth = null,
  windowDays = 90,
}) => {
  const theme = useTheme();
  const summary = describeUnclaimed(leads.length, unclaimedTruth, windowDays);
  const [pending, setPending] = React.useState<Record<string, LeadActionKind>>({});
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const inFlight = React.useRef(new Set<string>());
  // First-shot countdowns re-render once a minute; the state machine itself is
  // pure (lib/leadOpportunity.firstShotState) and takes this as its clock.
  const [nowMs, setNowMs] = React.useState(() => Date.now());
  React.useEffect(() => {
    const timer = setInterval(() => setNowMs(Date.now()), 60_000);
    return () => clearInterval(timer);
  }, []);
  const handleAction = async (lead: DashboardLeadRow, action: LeadActionKind) => {
    if (inFlight.current.has(lead.id)) return;
    inFlight.current.add(lead.id);
    setPending((value) => ({ ...value, [lead.id]: action }));
    setErrors((value) => ({ ...value, [lead.id]: "" }));
    try {
      const result = await runLeadAction(lead.id, action);
      onResolved(lead, result.owner_user_id, action);
    } catch (error) {
      // EVENT-2179 · the `LeadConflictError` arm cannot fire today (see
      // leadRefusal.ts) and every other refusal fell through to `error.message`,
      // which for an ApiError is only `API error: <status>`.
      const message = leadActionErrorMessage(error, buildLeadDisplayName(lead));
      setErrors((value) => ({ ...value, [lead.id]: message }));
    } finally {
      inFlight.current.delete(lead.id);
      setPending((value) => { const next = { ...value }; delete next[lead.id]; return next; });
    }
  };

  return (
    <Card sx={{ display: "flex", flexDirection: "column", ...cardSx }}>
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          flex: 1,
          "&:last-child": { pb: 2 },
        }}
      >
        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
          <MoveToInboxIcon sx={{ color: theme.palette.primary.main }} />
          <Typography variant="h6" fontWeight={600}>
            New Leads
          </Typography>
          {summary.badge !== null && (
            <Chip
              label={summary.badge}
              size="small"
              data-testid="new-leads-unclaimed-badge"
              sx={{
                fontWeight: 700,
                bgcolor: alpha(theme.palette.primary.main, 0.14),
                color: "primary.main",
              }}
            />
          )}
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 1.5 }}
        >
          Highest revenue opportunities first · Davis gets first shot at top leads for 2h · manual claim only
        </Typography>
        {summary.hiddenNote && (
          <Typography
            variant="caption"
            color="warning.main"
            role="status"
            sx={{ display: "block", mb: 1 }}
          >
            {summary.hiddenNote}
          </Typography>
        )}

        {unavailable ? (
          <Alert severity="warning" role="status" sx={{ mt: 1 }}>
            Lead ranking is temporarily unavailable. Refresh to retry.
          </Alert>
        ) : loading && leads.length === 0 && !unclaimedTruth ? (
          // Nothing has loaded yet: say so. The checkmark + "all caught up"
          // below is a claim about the claim ledger and may only render after
          // a settled fetch.
          <Box py={3} textAlign="center">
            <Typography
              variant="body2"
              color="text.secondary"
              role="status"
              data-testid="new-leads-loading"
            >
              Loading unclaimed leads…
            </Typography>
          </Box>
        ) : leads.length > 0 ? (
          <>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                maxHeight: { xs: "50dvh", md: 420 },
                overflowY: "auto",
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            >
              {leads.map((lead) => (
                <LeadFeedRow
                  key={lead.id}
                  lead={lead}
                  nowMs={nowMs}
                  onRowClick={onRowClick}
                  pendingAction={pending[lead.id]}
                  error={errors[lead.id]}
                  onAction={(row, action) => void handleAction(row, action)}
                />
              ))}
            </Box>
            <Button
              size="small"
              endIcon={<ArrowForwardIcon />}
              onClick={onViewAll}
              sx={{
                mt: 1,
                alignSelf: "flex-start",
                textTransform: "none",
                fontWeight: 700,
                minHeight: TOUCH_TARGET,
              }}
            >
              View all leads
            </Button>
          </>
        ) : (
          <Box py={3} textAlign="center">
            <CheckCircleIcon
              sx={{
                fontSize: 36,
                color: theme.palette.success.main,
                opacity: 0.5,
                mb: 0.5,
              }}
            />
            <Typography variant="body2" color="text.secondary">
              {summary.empty}
            </Typography>
            {summary.empty !== "You're all caught up — no unclaimed leads." && (
              <Button
                size="small"
                endIcon={<ArrowForwardIcon />}
                onClick={onViewAll}
                sx={{ mt: 1, textTransform: "none", fontWeight: 700, minHeight: TOUCH_TARGET }}
              >
                View all leads
              </Button>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default NewLeadsFeed;
